"use client";

import { c as _c } from "react/compiler-runtime";
import { Gavel } from "lucide-react";
import { AuctionStory, Panel } from "@/features/auctions/v2/single/AuctionFacts";
import { Framed } from "@/features/auctions/v2/single/designs/DesignsA";
import JournalComments from "@/features/comments/components/JournalComments";
import NftActs from "./NftActs";
import { NftDesk, NftReceipts } from "./NftDesk";
import { NftCollection, NftMeta, NftOwner, NftRecordPanel, NftTitle, NftTraits } from "./NftFacts";
import { NftOffers } from "./NftOffers";
import { NftTabs, Region } from "./NftTabs";
import type { NftView } from "./nftView";
import type { NftFinance } from "./useNftFinance";
export type DesignProps = {
  v: NftView;
  f: NftFinance;
};
function Talk(t0) {
  const $ = _c(3);
  const {
    v
  } = t0;
  let t1;
  if ($[0] !== v.id || $[1] !== v.lang) {
    t1 = <section className="ns-talk"><JournalComments modelId={v.id} modelType="nft" lang={v.lang} /></section>;
    $[0] = v.id;
    $[1] = v.lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function Head(t0) {
  const $ = _c(11);
  const {
    v
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== v) {
    t1 = <NftMeta v={v} />;
    t2 = <NftTitle v={v} />;
    t3 = <NftCollection v={v} />;
    $[0] = v;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== v) {
    t4 = <div className="ns-head-row"><NftOwner v={v} /><NftActs v={v} /></div>;
    $[4] = v;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <header className="ns-head">{t1}{t2}{t3}{t4}</header>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function Desk(t0) {
  const $ = _c(7);
  const {
    f,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["\u0628\u062E\u0634 \u062E\u0631\u06CC\u062F \u0648 \u0641\u0631\u0648\u0634", "The buy and sell section"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className || $[2] !== f) {
    t2 = <NftDesk f={f} className={className} />;
    $[1] = className;
    $[2] = f;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== f.isFa || $[5] !== t2) {
    t3 = <Region isFa={f.isFa} what={t1}>{t2}</Region>;
    $[4] = f.isFa;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * ۴ — کلاسیک. The live page's own order, redrawn in v2: the work held still
 * on one side; on the other the name, then three underlined tabs — «نمای کلی»
 * (the counter, the offers, the traits, the record, the folded note),
 * activity, holders — and the talk under them, all in one column.
 */
export function D4Classic(t0) {
  const $ = _c(67);
  const {
    v,
    f
  } = t0;
  const isFa = v.lang === "fa";
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== f || $[1] !== isFa) {
    const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
    t7 = "ns-d4-overview";
    if ($[13] !== f) {
      t8 = <Desk f={f} className="d1-card" />;
      t9 = <NftReceipts f={f} />;
      $[13] = f;
      $[14] = t8;
      $[15] = t9;
    } else {
      t8 = $[14];
      t9 = $[15];
    }
    T1 = Region;
    t5 = isFa;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = ["\u0628\u062E\u0634 \u0622\u0641\u0631\u0647\u0627", "The offers"];
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    T0 = Panel;
    t1 = "ns-offers-h";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Gavel aria-hidden={true} />;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    t3 = isFa ? "\u0622\u0641\u0631\u0647\u0627" : "Offers";
    t4 = !f.isOffersLoading && !f.isOffersError ? nf.format(f.offersTotalCount) : undefined;
    $[0] = f;
    $[1] = isFa;
    $[2] = T0;
    $[3] = T1;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    T0 = $[2];
    T1 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[18] !== f) {
    t10 = <NftOffers f={f} />;
    $[18] = f;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== T0 || $[21] !== t1 || $[22] !== t10 || $[23] !== t2 || $[24] !== t3 || $[25] !== t4) {
    t11 = <T0 id={t1} icon={t2} title={t3} count={t4}>{t10}</T0>;
    $[20] = T0;
    $[21] = t1;
    $[22] = t10;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== T1 || $[28] !== t11 || $[29] !== t5 || $[30] !== t6) {
    t12 = <T1 isFa={t5} what={t6}>{t11}</T1>;
    $[27] = T1;
    $[28] = t11;
    $[29] = t5;
    $[30] = t6;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  let t14;
  if ($[32] !== v) {
    t13 = <NftTraits v={v} fold="open" />;
    t14 = <NftRecordPanel v={v} fold="open" />;
    $[32] = v;
    $[33] = t13;
    $[34] = t14;
  } else {
    t13 = $[33];
    t14 = $[34];
  }
  let t15;
  if ($[35] !== v.description || $[36] !== v.lang) {
    t15 = v.description ? <AuctionStory text={v.description} lang={v.lang} fold="closed" /> : null;
    $[35] = v.description;
    $[36] = v.lang;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t16 = <div className={t7}>{t8}{t9}{t12}{t13}{t14}{t15}</div>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const overview = t16;
  const t17 = `url("${v.image}")`;
  let t18;
  if ($[46] !== t17) {
    t18 = {
      "--ap-art": t17
    };
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = t18 as React.CSSProperties;
  let t20;
  if ($[48] !== v.image || $[49] !== v.name) {
    t20 = <div className="d1-art"><Framed image={v.image} name={v.name} sizes="(max-width: 1023px) 92vw, 42vw" /></div>;
    $[48] = v.image;
    $[49] = v.name;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== v) {
    t21 = <Head v={v} />;
    $[51] = v;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== f || $[54] !== overview || $[55] !== v) {
    t22 = <NftTabs v={v} f={f} overview={overview} />;
    $[53] = f;
    $[54] = overview;
    $[55] = v;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== v) {
    t23 = <Talk v={v} />;
    $[57] = v;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t21 || $[60] !== t22 || $[61] !== t23) {
    t24 = <div className="d1-desk">{t21}{t22}{t23}</div>;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t19 || $[64] !== t20 || $[65] !== t24) {
    t25 = <div className="d1 ns-d1 ns-d4" style={t19}>{t20}{t24}</div>;
    $[63] = t19;
    $[64] = t20;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  return t25;
}